import {useState, useContext} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import {type UserApp} from '@taruvi/sdk';
import {SpaceContext} from '../SpaceProvider.tsx';

const ApplicationsView = () => {
  const [search, setSearch] = useState('');
  const ctx = useContext(SpaceContext);
  const apps = ctx?.[8] || [];

  return (
    <Box sx={{p: 4}}>
      <Typography sx={{fontFamily: '"Quicksand", sans-serif', fontWeight: 700, fontSize: '1.5rem', mb: 1}}>
        Applications
      </Typography>
      <Typography sx={{color: '#64748b', mb: 3, maxWidth: 500}}>
        Manage your digital ecosystem. Launch integrated tools and explore new productivity modules.
      </Typography>

      <TextField
        size="small"
        placeholder="Search applications..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{mb: 3, maxWidth: 400, bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 3, '& .MuiOutlinedInput-root': {borderRadius: 3}}}
        slotProps={{input: {startAdornment: <InputAdornment position="start"><SearchIcon sx={{color: '#9ca3af', fontSize: 20}} /></InputAdornment>}}}
      />

      <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3}}>
        {apps.filter(a => {
          if (!search) return true;
          const q = search.toLowerCase();
          return (a.display_name || a.name || '').toLowerCase().includes(q) || a.slug.toLowerCase().includes(q);
        }).map(app => (
          <Box
            key={app.slug}
            component="a"
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              bgcolor: 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(192,199,213,0.15)',
              borderRadius: 4,
              overflow: 'hidden',
              transition: 'all 0.3s',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              position: 'relative',
              '&:hover': {
                boxShadow: '0 8px 32px rgba(66,133,244,0.08)',
              },
            }}
          >
            <OpenInNewIcon sx={{position: 'absolute', top: 16, right: 16, fontSize: 18, color: '#c0c4cc'}} />
            <Box sx={{p: 3, display: 'flex', flexDirection: 'column', gap: 2}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                {app.icon ? (
                  <Box
                    component="img"
                    src={app.icon}
                    alt=""
                    sx={{width: 40, height: 40, borderRadius: 2.5, objectFit: 'contain', flexShrink: 0}}
                  />
                ) : (
                  <Box sx={{
                    width: 40, height: 40, borderRadius: 2.5, flexShrink: 0,
                    bgcolor: '#2b97ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, color: '#fff', fontSize: 16,
                    fontFamily: '"Quicksand", sans-serif',
                  }}>
                    {(app.display_name || app.name || '?')[0].toUpperCase()}
                  </Box>
                )}
                <Typography sx={{fontFamily: '"Quicksand", sans-serif', fontWeight: 700, fontSize: 16}}>
                  {app.display_name || app.name}
                </Typography>
              </Box>
              <Typography sx={{fontSize: 14, color: '#64748b', lineHeight: 1.6, letterSpacing: 0.5}}>
                {app.description}
              </Typography>
            </Box>
          </Box>
        ))}

        {apps.length === 0 && (
          <Typography sx={{color: '#9ca3af', gridColumn: '1 / -1', textAlign: 'center', py: 6}}>
            No applications found
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ApplicationsView;
